import React, { Fragment, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import AdministrationHeader from "../../components/header-components/AdministrationHeader";
import FlexTable from "../../components/ui-components/FlexTable";
import TableCard from "../../components/ui-components/TableCard";
import EmptyState from "../../components/ui-components/EmptyState";
import StatusPill from "../Budgets/components/StatusPill";
import { listCommitments } from "./api";

const eurFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "EUR",
});

const formatEur = (value) =>
  typeof value === "number" ? eurFormatter.format(value) : "—";

// formatRenewalDate renders the raw RFC3339 end_timestamp as a readable date; falls back
// to "—" for an empty or unparseable value rather than showing "Invalid Date".
const formatRenewalDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

// CommitmentsListPage — read-only view of active GCP committed-use discount exposure
// (Board Report v2 SP3), sourced from GET /api/v1/commitments. No row navigation, no
// search/filter — this is a plain list.
const CommitmentsListPage = () => {
  const { data, isLoading, error, refetch } = useQuery(
    ["commitments"],
    listCommitments
  );

  const commitments = data || [];

  const columnDefs = useMemo(
    () => [
      {
        id: "resource",
        accessor: "resource",
        Header: () => <span>Resource</span>,
        Cell: ({ value }) => (
          <span className="font-medium text-text truncate">{value || "—"}</span>
        ),
        flex: 1,
        minWidth: 200,
      },
      {
        id: "plan",
        accessor: "plan",
        Header: () => <span>Plan</span>,
        Cell: ({ value }) => <span className="text-text">{value || "—"}</span>,
        width: 140,
        minWidth: 110,
        maxWidth: 200,
      },
      {
        id: "committed_amount",
        accessor: "committed_amount",
        align: "right",
        Header: () => <span>Committed</span>,
        Cell: ({ value }) => (
          <span className="tabular-nums text-text">{formatEur(value)}</span>
        ),
        width: 140,
        minWidth: 110,
        maxWidth: 180,
      },
      {
        id: "renewal_date",
        accessor: "renewal_date",
        Header: () => <span>Renewal</span>,
        Cell: ({ row }) => {
          const { renewal_date, renews_within_horizon } = row.original;
          return (
            <span
              className={`tabular-nums ${
                renews_within_horizon ? "font-semibold text-warning" : "text-text"
              }`}
              title={renews_within_horizon ? "Renews within the board horizon" : undefined}
            >
              {formatRenewalDate(renewal_date)}
              {renews_within_horizon && (
                <span className="ml-1.5 inline-flex items-center rounded-full bg-warning-soft px-1.5 py-0.5 text-[10px] font-semibold text-warning align-middle">
                  Upcoming
                </span>
              )}
            </span>
          );
        },
        width: 200,
        minWidth: 160,
        maxWidth: 240,
      },
      {
        id: "exposure",
        accessor: "exposure",
        align: "right",
        Header: () => <span>Exposure</span>,
        Cell: ({ value }) => (
          <span className="tabular-nums text-text">{formatEur(value)}</span>
        ),
        width: 140,
        minWidth: 110,
        maxWidth: 180,
      },
      {
        id: "status",
        accessor: "status",
        align: "center",
        Header: () => <span>Status</span>,
        Cell: ({ value }) => <StatusPill status={value?.toLowerCase()} />,
        width: 120,
        minWidth: 100,
        maxWidth: 140,
      },
    ],
    []
  );

  return (
    <Fragment>
      <AdministrationHeader title="Commitments" className="text-text-3" showPeriodSelector={false} />
      <div className="w-full p-8">
        <TableCard
          title="Commitments"
          subtitle="Active GCP committed-use discounts and renewal exposure"
          isLoading={isLoading}
          error={error}
          errorMessage="Error loading commitments"
          onRetry={() => refetch()}
        >
          {commitments.length === 0 ? (
            <EmptyState
              icon={icon({ name: "file-contract", style: "light" })}
              title="No commitments"
              message="GCP committed-use discounts will appear here once ingested."
            />
          ) : (
            <FlexTable
              columns={columnDefs}
              data={commitments}
              getRowId={(c) => `${c.project_id}::${c.resource}`}
            />
          )}
        </TableCard>
      </div>
    </Fragment>
  );
};

export default CommitmentsListPage;
